import React, { useState, useContext } from 'react'
import Sidedash from '../uicomponents/Sidedash'
import AlertContext from '../../context/AlertContext'
import SeatContext from '../../context/SeatContext'

const AddSeat = () => {
  const { showAlert } = useContext(AlertContext)
  const { addSeat, addBulkSeats } = useContext(SeatContext)
  const [formType, setFormType] = useState('single') // 'single' or 'bulk'
  const [loading, setLoading] = useState(false)
  
  // Single seat form data
  const [singleFormData, setSingleFormData] = useState({ 
    seatNumber: '', 
    seatLocation: '' 
  })
  
  // Bulk seat form data
  const [bulkFormData, setBulkFormData] = useState({ 
    startingValue: '', 
    numberOfSeats: '', 
    seatLocation: '' 
  })

  const handleSingleFormChange = (e) => {
    const { name, value } = e.target
    setSingleFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleBulkFormChange = (e) => {
    const { name, value } = e.target
    setBulkFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSingleSubmit = async (e) => {
    e.preventDefault()
    const { seatNumber, seatLocation } = singleFormData
    if (!seatNumber || !seatLocation) {
      showAlert('Please fill in both fields', 'warning')
      return
    }
    
    setLoading(true)
    try {
      const result = await addSeat(seatNumber, seatLocation)
      if (result.success) {
        showAlert('Seat added successfully', 'success')
        setSingleFormData({ seatNumber: '', seatLocation: '' })
      } else {
        showAlert(`Error adding seat: ${result.message}`, 'danger')
      }
    } catch (error) {
      showAlert('An error occurred while adding the seat', 'danger')
    } finally {
      setLoading(false)
    }
  }

  const handleBulkSubmit = async (e) => {
    e.preventDefault()
    const { startingValue, numberOfSeats, seatLocation } = bulkFormData
    if (!startingValue || !numberOfSeats || !seatLocation) {
      showAlert('Please fill in all fields', 'warning')
      return
    }
    
    const numSeats = parseInt(numberOfSeats, 10)
    if (numSeats < 1 || numSeats > 100) {
      showAlert('Number of seats must be between 1 and 100', 'warning')
      return
    }
    
    setLoading(true)
    try {
      const result = await addBulkSeats(startingValue, numSeats, seatLocation)
      if (result.success) {
        showAlert(result.message, 'success')
        setBulkFormData({ startingValue: '', numberOfSeats: '', seatLocation: '' })
      } else {
        showAlert(`Error adding seats: ${result.message}`, 'danger')
      }
    } catch (error) {
      showAlert('An error occurred while adding the seats', 'danger')
    } finally {
      setLoading(false)
    }
  }

  // Function to generate preview of seat numbers
  const generatePreview = () => {
    const { startingValue, numberOfSeats } = bulkFormData
    if (!startingValue || !numberOfSeats) return []
    
    const numSeats = parseInt(numberOfSeats, 10)
    if (numSeats < 1 || numSeats > 100) return []
    
    const seats = []
    // Extract the base (non-numeric part) and the numeric part
    const match = startingValue.match(/^([A-Za-z]*)(\d*)$/)
    const baseValue = match ? match[1] : ''
    const startNumber = match && match[2] ? parseInt(match[2]) : 0
    
    for (let i = 0; i < Math.min(numSeats, 10); i++) {
      const seatNumber = baseValue + (startNumber + i)
      seats.push(seatNumber)
    }
    
    if (numSeats > 10) {
      seats.push('...')
      const lastSeatNumber = baseValue + (startNumber + numSeats - 1)
      seats.push(lastSeatNumber)
    }
    
    return seats
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <Sidedash />
        <div className="col-md-9 pt-3">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Add New Seats</h2>
            <div className="btn-group" role="group">
              <input
                type="radio"
                className="btn-check"
                name="formType"
                id="single"
                checked={formType === 'single'}
                onChange={() => setFormType('single')}
              />
              <label className="btn btn-outline-primary" htmlFor="single">
                Single Seat
              </label>
              
              <input
                type="radio"
                className="btn-check"
                name="formType"
                id="bulk"
                checked={formType === 'bulk'}
                onChange={() => setFormType('bulk')}
              />
              <label className="btn btn-outline-primary" htmlFor="bulk">
                Bulk Seats
              </label>
            </div>
          </div>

          {formType === 'single' ? (
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">Add Single Seat</h5>
                <form onSubmit={handleSingleSubmit}>
                  <div className="mb-3">
                    <label htmlFor="seatNumber" className="form-label">Seat Number</label>
                    <input
                      type="text"
                      className="form-control"
                      id="seatNumber"
                      name="seatNumber"
                      value={singleFormData.seatNumber}
                      onChange={handleSingleFormChange}
                      placeholder="e.g., A1, B2, W4"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="seatLocation" className="form-label">Seat Location</label>
                    <input
                      type="text"
                      className="form-control"
                      id="seatLocation"
                      name="seatLocation"
                      value={singleFormData.seatLocation}
                      onChange={handleSingleFormChange}
                      placeholder="e.g., Main Hall, Waiting Block"
                      required
                    />
                  </div>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Adding...' : 'Add Seat'}
                  </button>
                </form>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">Add Bulk Seats</h5>
                <form onSubmit={handleBulkSubmit}>
                  <div className="row">
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label htmlFor="startingValue" className="form-label">Starting Value</label>
                        <input
                          type="text"
                          className="form-control"
                          id="startingValue"
                          name="startingValue"
                          value={bulkFormData.startingValue}
                          onChange={handleBulkFormChange}
                          placeholder="e.g., W4, A1, B10"
                          required
                        />
                        <div className="form-text">Can be alphanumeric (e.g., W4, A1, B10)</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label htmlFor="numberOfSeats" className="form-label">Number of Seats</label>
                        <input
                          type="number"
                          className="form-control"
                          id="numberOfSeats"
                          name="numberOfSeats"
                          value={bulkFormData.numberOfSeats}
                          onChange={handleBulkFormChange}
                          placeholder="e.g., 20"
                          min="1"
                          max="100"
                          required
                        />
                        <div className="form-text">Maximum 100 seats at once</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label htmlFor="bulkSeatLocation" className="form-label">Seat Location</label>
                        <input
                          type="text"
                          className="form-control"
                          id="bulkSeatLocation"
                          name="seatLocation"
                          value={bulkFormData.seatLocation}
                          onChange={handleBulkFormChange}
                          placeholder="e.g., Waiting Block"
                          required
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Preview Section */}
                  {bulkFormData.startingValue && bulkFormData.numberOfSeats && (
                    <div className="mb-3">
                      <label className="form-label">Preview:</label>
                      <div className="alert alert-info">
                        <strong>Seats to be created:</strong> {generatePreview().join(', ')}
                      </div>
                    </div>
                  )}
                  
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Adding...' : 'Add Bulk Seats'}
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AddSeat 